/**
 * LinkedDeviceProvisioning.ts
 *
 * Pure-crypto envelope + QR URL codec for linked-device provisioning.
 *
 * ## Wire format (canonical — iOS LinkedDeviceProvisioning.swift matches this exactly)
 *
 * ### QR URL  (shown by the NEW device)
 *   `kaether:link:v1:<base64url-no-padding>`
 *
 *   The base64url payload is the binary concatenation:
 *     - sessionId    : 16 bytes  (random UUID v4 raw bytes)
 *     - ephemeralPub : 32 bytes  (X25519 raw public key, RFC 7748 little-endian)
 *     - nonce        : 12 bytes  (AES-GCM nonce, random)
 *   Total: 60 bytes
 *
 * ### Sealing  (EXISTING device → new device, after scanning the QR)
 *   The existing device generates its own fresh X25519 ephemeral key pair
 *   (existingEphPriv, existingEphPub) for each provisioning operation.
 *
 *   dhSecret    = X25519(existingEphPriv, newDeviceEphPub)   // 32 bytes
 *   salt        = 0x00 × 32
 *   info        = "kaether:linked-device:v1"
 *   wrappingKey = HKDF-SHA256(ikm=dhSecret, salt, info)      // AES-256-GCM key
 *   aad         = sessionId ‖ newDeviceEphPub                // 28 bytes
 *   ciphertext  = AES-GCM-Seal(bundleJSON, wrappingKey, nonce=qr.nonce, aad)
 *
 *   SealedProvisioning = { ciphertext, aad, existingDeviceEphPub }
 *   `existingDeviceEphPub` is transmitted in plain alongside the ciphertext so
 *   the new device can perform the symmetric ECDH.
 *
 * ### Opening  (NEW device side)
 *   dhSecret    = X25519(newDeviceEphPriv, existingDeviceEphPub)  // same secret
 *   wrappingKey = HKDF-SHA256(dhSecret, ...)
 *   bundleJSON  = AES-GCM-Open(ciphertext, wrappingKey, nonce=qr.nonce, aad)
 *
 * ### Provisioning bundle (JSON inside ciphertext)
 *   {
 *     identityPublicKey    : string  // base64 — existing device's signing key
 *     identityPrivateKey   : string  // base64
 *     signedPreKeyId       : number
 *     signedPreKeyPublic   : string  // base64
 *     signedPreKeyPrivate  : string  // base64
 *     signedPreKeySignature: string  // base64
 *     registrationId       : number
 *     deviceId             : number
 *     onboardingVersion    : 1
 *   }
 *
 * ## Cross-platform parity guarantee
 *   QR URL scheme, byte layout, HKDF info string, salt, and AAD construction
 *   are identical between this file and iOS LinkedDeviceProvisioning.swift.
 *
 * ## Scope
 *   Pure crypto + URL codec ONLY.
 *   No QR scanner UI. No server fetch. No <canvas> QR rendering. No SolidJS.
 */

// ---------------------------------------------------------------------------
// Value types
// ---------------------------------------------------------------------------

/** Data encoded in the QR code displayed by the new device. */
export interface ProvisioningQR {
  /** 16 random bytes — identifies this provisioning session. */
  readonly sessionId: Uint8Array;
  /** 32-byte X25519 ephemeral public key generated by the new device. */
  readonly ephemeralPub: Uint8Array;
  /** 12-byte AES-GCM nonce, randomly generated by the new device. */
  readonly nonce: Uint8Array;
}

/** Account-state payload transferred from the existing device to the new device. */
export interface ProvisioningBundle {
  /** base64 X25519 identity public key. */
  readonly identityPublicKey: string;
  /** base64 X25519 identity private key. */
  readonly identityPrivateKey: string;
  readonly signedPreKeyId: number;
  /** base64 X25519 signed pre-key public component. */
  readonly signedPreKeyPublic: string;
  /** base64 X25519 signed pre-key private component. */
  readonly signedPreKeyPrivate: string;
  /** base64 Ed25519 signature over signedPreKeyPublic bytes. */
  readonly signedPreKeySignature: string;
  readonly registrationId: number;
  readonly deviceId: number;
  /** Schema version sentinel. Always 1 for this release. */
  readonly onboardingVersion: 1;
}

/**
 * The encrypted form of a ProvisioningBundle, ready to post to the server
 * provisioning channel (keyed by sessionId).
 */
export interface SealedProvisioning {
  /**
   * AES-256-GCM ciphertext with the 16-byte auth tag appended
   * (SubtleCrypto convention — tag is at the end of the buffer).
   * Does NOT include the nonce; the receiver recovers it from the QR.
   */
  readonly ciphertext: Uint8Array;
  /**
   * AAD used during sealing: sessionId ‖ newDeviceEphPub (28 bytes).
   * Carried alongside so the receiver can verify byte-exact consistency
   * without re-parsing the QR.
   */
  readonly aad: Uint8Array;
  /**
   * The existing device's X25519 ephemeral public key (32 bytes).
   * Transmitted in plain alongside the ciphertext so the new device can
   * reconstruct the same ECDH shared secret.
   */
  readonly existingDeviceEphPub: Uint8Array;
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const QR_URL_PREFIX = "kaether:link:v1:";
const HKDF_INFO = new TextEncoder().encode("kaether:linked-device:v1");
const HKDF_SALT = new Uint8Array(32); // 0x00 × 32 — matches iOS SignalCrypto.deriveKey

const SESSION_ID_BYTES = 16;
const EPHEMERAL_PUB_BYTES = 32;
const NONCE_BYTES = 12;
/** Total bytes in the binary QR payload. */
const QR_PAYLOAD_BYTES = SESSION_ID_BYTES + EPHEMERAL_PUB_BYTES + NONCE_BYTES; // 60

// ---------------------------------------------------------------------------
// Internal codec helpers
// ---------------------------------------------------------------------------

/** base64url (no padding) encode. */
function toBase64url(bytes: Uint8Array): string {
  let binary = "";
  for (let i = 0; i < bytes.length; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

/** base64url (no padding) decode. Throws on invalid characters. */
function fromBase64url(str: string): Uint8Array {
  const padded = str.replace(/-/g, "+").replace(/_/g, "/");
  const padLen = (4 - (padded.length % 4)) % 4;
  const base64 = padded + "=".repeat(padLen);
  let binary: string;
  try {
    binary = atob(base64);
  } catch {
    throw new Error("LinkedDeviceProvisioning: invalid base64url encoding in QR URL");
  }
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes;
}

/** Standard (padded) base64 encode — used for bundle field serialisation. */
export function toBase64(bytes: Uint8Array): string {
  let binary = "";
  for (let i = 0; i < bytes.length; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary);
}

/** Standard (padded) base64 decode — used when reconstructing bundle fields. */
export function fromBase64(str: string): Uint8Array {
  let binary: string;
  try {
    binary = atob(str);
  } catch {
    throw new Error("LinkedDeviceProvisioning: invalid base64 in bundle field");
  }
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes;
}

/** Build the 28-byte AAD: sessionId ‖ newDeviceEphPub. */
function buildAAD(qr: ProvisioningQR): Uint8Array {
  const aad = new Uint8Array(SESSION_ID_BYTES + EPHEMERAL_PUB_BYTES);
  aad.set(qr.sessionId, 0);
  aad.set(qr.ephemeralPub, SESSION_ID_BYTES);
  return aad;
}

/**
 * X25519 ECDH via SubtleCrypto.
 * Accepts raw 32-byte private scalar and raw 32-byte public key.
 * Returns raw 32-byte shared secret.
 */
async function x25519ECDH(
  privateKeyScalar: Uint8Array,
  publicKeyBytes: Uint8Array,
): Promise<Uint8Array> {
  // SubtleCrypto requires PKCS#8 for X25519 private key import.
  // Build a minimal PKCS#8 DER wrapper around the raw 32-byte scalar.
  // DER header for X25519: 30 2e 02 01 00 30 05 06 03 2b 65 6e 04 22 04 20
  //   (OID 1.3.101.110 = id-X25519, version=0, private key octet string 32 bytes)
  const pkcs8Header = new Uint8Array([
    0x30, 0x2e,             // SEQUENCE, length 46
    0x02, 0x01, 0x00,       // INTEGER version = 0
    0x30, 0x05,             // SEQUENCE (algorithm identifier)
    0x06, 0x03, 0x2b, 0x65, 0x6e, // OID 1.3.101.110 (id-X25519)
    0x04, 0x22,             // OCTET STRING, length 34
    0x04, 0x20,             // OCTET STRING, length 32 (the actual key)
  ]);
  const pkcs8 = new Uint8Array(pkcs8Header.length + privateKeyScalar.length);
  pkcs8.set(pkcs8Header, 0);
  pkcs8.set(privateKeyScalar, pkcs8Header.length);

  const privKey = await crypto.subtle.importKey(
    "pkcs8",
    pkcs8,
    { name: "X25519" },
    false,
    ["deriveBits"],
  );
  const pubKey = await crypto.subtle.importKey(
    "raw",
    publicKeyBytes,
    { name: "X25519" },
    false,
    [],
  );
  const bits = await crypto.subtle.deriveBits(
    { name: "X25519", public: pubKey },
    privKey,
    256,
  );
  return new Uint8Array(bits);
}

/**
 * HKDF-SHA256: IKM (32 bytes) → AES-256-GCM CryptoKey.
 * salt = 0x00 × 32, info = "kaether:linked-device:v1".
 */
async function hkdfDeriveKey(ikm: Uint8Array): Promise<CryptoKey> {
  const ikmKey = await crypto.subtle.importKey(
    "raw",
    ikm,
    { name: "HKDF" },
    false,
    ["deriveKey"],
  );
  return crypto.subtle.deriveKey(
    {
      name: "HKDF",
      hash: "SHA-256",
      salt: HKDF_SALT,
      info: HKDF_INFO,
    },
    ikmKey,
    { name: "AES-GCM", length: 256 },
    false,
    ["encrypt", "decrypt"],
  );
}

/** Assert that `raw` has all required ProvisioningBundle fields. */
function validateBundle(raw: unknown): asserts raw is ProvisioningBundle {
  if (typeof raw !== "object" || raw === null) {
    throw new Error("LinkedDeviceProvisioning: bundle is not an object");
  }
  const obj = raw as Record<string, unknown>;

  const stringFields = [
    "identityPublicKey",
    "identityPrivateKey",
    "signedPreKeyPublic",
    "signedPreKeyPrivate",
    "signedPreKeySignature",
  ] as const;
  for (const field of stringFields) {
    if (typeof obj[field] !== "string" || (obj[field] as string).length === 0) {
      throw new Error(
        `LinkedDeviceProvisioning: bundle missing or empty string field "${field}"`,
      );
    }
  }

  const numberFields = ["signedPreKeyId", "registrationId", "deviceId"] as const;
  for (const field of numberFields) {
    if (typeof obj[field] !== "number") {
      throw new Error(
        `LinkedDeviceProvisioning: bundle missing or non-numeric field "${field}"`,
      );
    }
  }

  if (obj["onboardingVersion"] !== 1) {
    throw new Error(
      `LinkedDeviceProvisioning: unsupported onboardingVersion` +
      ` "${String(obj["onboardingVersion"])}" (expected 1)`,
    );
  }
}

// ---------------------------------------------------------------------------
// Public API — QR codec
// ---------------------------------------------------------------------------

/**
 * Encode a ProvisioningQR to its canonical URL string.
 *
 * Format: `kaether:link:v1:<base64url(sessionId[16] ‖ ephemeralPub[32] ‖ nonce[12])>`
 */
export function encodeQR(qr: ProvisioningQR): string {
  if (qr.sessionId.length !== SESSION_ID_BYTES) {
    throw new Error(
      `LinkedDeviceProvisioning: sessionId must be ${SESSION_ID_BYTES} bytes`,
    );
  }
  if (qr.ephemeralPub.length !== EPHEMERAL_PUB_BYTES) {
    throw new Error(
      `LinkedDeviceProvisioning: ephemeralPub must be ${EPHEMERAL_PUB_BYTES} bytes`,
    );
  }
  if (qr.nonce.length !== NONCE_BYTES) {
    throw new Error(
      `LinkedDeviceProvisioning: nonce must be ${NONCE_BYTES} bytes`,
    );
  }
  const payload = new Uint8Array(QR_PAYLOAD_BYTES);
  payload.set(qr.sessionId, 0);
  payload.set(qr.ephemeralPub, SESSION_ID_BYTES);
  payload.set(qr.nonce, SESSION_ID_BYTES + EPHEMERAL_PUB_BYTES);
  return QR_URL_PREFIX + toBase64url(payload);
}

/**
 * Decode a QR URL string back to a ProvisioningQR.
 *
 * Throws if:
 *  - URL does not start with `kaether:link:v1:`
 *  - The base64url payload decodes to anything other than 60 bytes
 *  - The base64url string is malformed
 */
export function decodeQR(url: string): ProvisioningQR {
  if (!url.startsWith(QR_URL_PREFIX)) {
    throw new Error(
      `LinkedDeviceProvisioning: QR URL must start with "${QR_URL_PREFIX}"`,
    );
  }
  const encoded = url.slice(QR_URL_PREFIX.length);
  const payload = fromBase64url(encoded);
  if (payload.length !== QR_PAYLOAD_BYTES) {
    throw new Error(
      `LinkedDeviceProvisioning: QR payload must be ${QR_PAYLOAD_BYTES} bytes,` +
      ` got ${payload.length}`,
    );
  }
  return {
    sessionId: payload.slice(0, SESSION_ID_BYTES),
    ephemeralPub: payload.slice(SESSION_ID_BYTES, SESSION_ID_BYTES + EPHEMERAL_PUB_BYTES),
    nonce: payload.slice(SESSION_ID_BYTES + EPHEMERAL_PUB_BYTES),
  };
}

// ---------------------------------------------------------------------------
// Public API — ephemeral key generation helpers
// ---------------------------------------------------------------------------

/**
 * Generate a fresh ProvisioningQR and the matching ephemeral private key.
 *
 * Call this on the NEW device. Keep `ephemeralPrivKey` secret; pass it later
 * to `openProvisioning`. Encode `qr` into a QR image for the existing device
 * to scan.
 */
export async function generateProvisioningQR(): Promise<{
  qr: ProvisioningQR;
  ephemeralPrivKey: Uint8Array;
}> {
  const { privKey, pubKey } = await generateEphemeralKeyPair();
  const sessionId = crypto.getRandomValues(new Uint8Array(SESSION_ID_BYTES));
  const nonce = crypto.getRandomValues(new Uint8Array(NONCE_BYTES));
  return {
    qr: { sessionId, ephemeralPub: pubKey, nonce },
    ephemeralPrivKey: privKey,
  };
}

/**
 * Generate a raw X25519 ephemeral key pair (32-byte private scalar +
 * 32-byte public key).
 *
 * Used by both sides. The existing device calls this to get its ephemeral key
 * pair before calling `sealProvisioning`.
 */
export async function generateEphemeralKeyPair(): Promise<{
  privKey: Uint8Array;
  pubKey: Uint8Array;
}> {
  const kp = await crypto.subtle.generateKey(
    { name: "X25519" },
    true, // extractable
    ["deriveBits"],
  );
  const pubRaw = new Uint8Array(await crypto.subtle.exportKey("raw", kp.publicKey));
  const privPkcs8 = new Uint8Array(await crypto.subtle.exportKey("pkcs8", kp.privateKey));
  // PKCS#8 DER for X25519: 16-byte header + 32-byte scalar.
  const privKey = privPkcs8.slice(16, 48);
  return { privKey, pubKey: pubRaw };
}

// ---------------------------------------------------------------------------
// Public API — crypto envelope
// ---------------------------------------------------------------------------

/**
 * Seal a ProvisioningBundle for delivery to the new device.
 *
 * Called on the EXISTING device after scanning the new device's QR code.
 *
 * @param bundle                 Account state to transfer.
 * @param qr                     Decoded from the new device's QR code.
 * @param existingDeviceEphPriv  Raw 32-byte private scalar of the existing
 *                               device's fresh ephemeral key pair.
 * @param existingDeviceEphPub   Matching 32-byte public key (transmitted in
 *                               plain alongside the ciphertext so the new
 *                               device can perform the ECDH open).
 */
export async function sealProvisioning(
  bundle: ProvisioningBundle,
  qr: ProvisioningQR,
  existingDeviceEphPriv: Uint8Array,
  existingDeviceEphPub: Uint8Array,
): Promise<SealedProvisioning> {
  // 1. ECDH(existingEphPriv, newDeviceEphPub) → 32-byte shared secret.
  const dhSecret = await x25519ECDH(existingDeviceEphPriv, qr.ephemeralPub);

  // 2. HKDF-SHA256 → AES-256-GCM wrapping key.
  const wrappingKey = await hkdfDeriveKey(dhSecret);

  // 3. JSON-encode the bundle.
  const plaintext = new TextEncoder().encode(JSON.stringify(bundle));

  // 4. AAD = sessionId ‖ newDeviceEphPub (28 bytes).
  const aad = buildAAD(qr);

  // 5. AES-GCM seal. SubtleCrypto appends the 16-byte tag.
  const ciphertextBuf = await crypto.subtle.encrypt(
    { name: "AES-GCM", iv: qr.nonce, additionalData: aad },
    wrappingKey,
    plaintext,
  );

  return {
    ciphertext: new Uint8Array(ciphertextBuf),
    aad,
    existingDeviceEphPub,
  };
}

/**
 * Open a SealedProvisioning and recover the ProvisioningBundle.
 *
 * Called on the NEW device after receiving the sealed bundle from the server
 * provisioning channel.
 *
 * Throws if:
 *  - GCM auth tag is invalid (tampered ciphertext or wrong key).
 *  - `sealed.aad` does not match the QR (session-id or ephemeral-pub mismatch).
 *  - The decrypted JSON is missing required fields or has wrong onboardingVersion.
 *
 * @param sealed                  The SealedProvisioning received from the server.
 * @param newDeviceEphemeralPrivKey  Raw 32-byte private scalar from `generateProvisioningQR`.
 * @param qr                      The ProvisioningQR that was encoded in the QR code.
 */
export async function openProvisioning(
  sealed: SealedProvisioning,
  newDeviceEphemeralPrivKey: Uint8Array,
  qr: ProvisioningQR,
): Promise<ProvisioningBundle> {
  // 1. Verify AAD matches the QR (guards against session-id replay / swap attacks).
  const expectedAAD = buildAAD(qr);
  if (sealed.aad.length !== expectedAAD.length) {
    throw new Error(
      "LinkedDeviceProvisioning: AAD length mismatch — " +
      "sealed bundle does not belong to this provisioning session",
    );
  }
  for (let i = 0; i < expectedAAD.length; i++) {
    if (sealed.aad[i] !== expectedAAD[i]) {
      throw new Error(
        "LinkedDeviceProvisioning: AAD byte mismatch at offset " + i +
        " — sealed bundle belongs to a different session or uses a different ephemeral key",
      );
    }
  }

  // 2. ECDH(newDeviceEphPriv, existingDeviceEphPub) → same 32-byte shared secret.
  const dhSecret = await x25519ECDH(
    newDeviceEphemeralPrivKey,
    sealed.existingDeviceEphPub,
  );

  // 3. HKDF-SHA256 → AES-256-GCM wrapping key.
  const wrappingKey = await hkdfDeriveKey(dhSecret);

  // 4. AES-GCM open. Throws DOMException on auth-tag failure.
  let plaintextBuf: ArrayBuffer;
  try {
    plaintextBuf = await crypto.subtle.decrypt(
      { name: "AES-GCM", iv: qr.nonce, additionalData: sealed.aad },
      wrappingKey,
      sealed.ciphertext,
    );
  } catch {
    throw new Error(
      "LinkedDeviceProvisioning: AES-GCM authentication failed — " +
      "ciphertext was tampered or the wrong ephemeral key pair was used",
    );
  }

  // 5. JSON decode + validate.
  const json = new TextDecoder().decode(plaintextBuf);
  let parsed: unknown;
  try {
    parsed = JSON.parse(json) as unknown;
  } catch {
    throw new Error(
      "LinkedDeviceProvisioning: decrypted bundle is not valid JSON",
    );
  }

  validateBundle(parsed);
  return parsed;
}
